/**
 * AccountabilitySection — the SHARED creation-time accountability block embedded by every
 * interactive Agentlet create surface (currently AgentCreateSlideout for Let / ExteLet). It
 * captures the accountable-owner choice into `form.accountability`, which the parent passes to
 * api.createAgent; the backend's shared OwnershipRepresentationService.establishOnCreate applies it:
 *   self + accept  → DISCOVERED_UNOWNED → claim(you) → attest → ACTIVE on create
 *   nominate someone→ DISCOVERED_UNOWNED → claim(them) → NOMINATED (inactive until they accept)
 *   (absent)        → DISCOVERED_UNOWNED (decide later via the ceremony)
 *
 * System agents (SysLet) are ownership-exempt, so the parent does not render this for that archetype.
 * Additive: if the section is never touched, the default is self+accept (creator owns what they build).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ShieldCheck, UserPlus, Search, BadgeCheck } from 'lucide-react';
import { Button } from './Button';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

/* Re-exported so existing importers (ExteletRegistrationWizard) are untouched.
   The shape is owned by services/accountability.js — a service needs it too, and
   a second copy of three fields is how two surfaces come to disagree about who
   accepted what. */
export { DEFAULT_ACCOUNTABILITY } from '../../services/accountability';
import { DEFAULT_ACCOUNTABILITY } from '../../services/accountability';

function OwnerCard({ selected, onClick, icon, title, desc }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-3 rounded-v2-md border transition-colors ${
        selected ? 'border-v2-action bg-v2-action-soft' : 'border-v2-border bg-v2-surface hover:border-v2-border-strong'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={selected ? 'text-v2-action' : 'text-v2-ink-muted'}>{icon}</span>
        <span className="text-[13px] font-semibold text-v2-ink">{title}</span>
      </div>
      <div className="text-[11.5px] text-v2-ink-muted leading-snug">{desc}</div>
    </button>
  );
}

export default function AccountabilitySection({ form, setForm }) {
  const { user, session } = useAuth();
  const myId = user?.id || session?.userId || null;
  const myName = user?.name || user?.email || 'Me';

  const acct = form?.accountability || DEFAULT_ACCOUNTABILITY;
  const update = useCallback((patch) => {
    setForm({ ...form, accountability: { ...DEFAULT_ACCOUNTABILITY, ...acct, ...patch } });
  }, [form, setForm, acct]);

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loadedUsers, setLoadedUsers] = useState(false);
  const [inviteMode, setInviteMode] = useState(false);
  const [invite, setInvite] = useState({ email: '', firstName: '', lastName: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [invitedOwner, setInvitedOwner] = useState(null); // { id, email } for a just-invited nominee

  useEffect(() => {
    if (acct.ownerMode !== 'nominate' || loadedUsers) return;
    let alive = true;
    (async () => {
      try {
        const body = unwrap(await api.getUsers({}));
        const list = Array.isArray(body) ? body : (Array.isArray(body?.content) ? body.content : []);
        if (alive) { setUsers(list); setLoadedUsers(true); }
      } catch (e) { /* leave empty */ }
    })();
    return () => { alive = false; };
  }, [acct.ownerMode, loadedUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q ? users.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q)) : users;
    return rows.slice(0, 30);
  }, [users, search]);

  const onInvite = useCallback(async () => {
    const email = (invite.email || '').trim();
    if (!email) { setInviteError('Email is required.'); return; }
    setInviting(true); setInviteError(null);
    try {
      // No agentId at creation time — the endpoint invites the person and returns their new id,
      // which we set as the nominee. The agent is then created NOMINATED to them.
      const res = await api.inviteOwnershipOwner({
        email,
        firstName: invite.firstName.trim() || undefined,
        lastName: invite.lastName.trim() || undefined,
        agentName: form?.name || undefined,
      });
      const body = (res && res.data !== undefined ? res.data : res) || {};
      const newId = body.ownerPrincipalId;
      if (newId) {
        setInvitedOwner({ id: newId, email });
        update({ ownerPrincipalId: newId });
        setInviteMode(false);
      } else {
        setInviteError('Invitation sent but no user id returned.');
      }
    } catch (e) {
      setInviteError(e?.status === 403 ? 'You do not have permission to invite a user.' : (e?.message || 'Could not send the invitation.'));
    } finally {
      setInviting(false);
    }
  }, [invite, form, update]);

  const selectedNominee = users.find((u) => u.id === acct.ownerPrincipalId);
  const nomineeLabel = selectedNominee
    ? (selectedNominee.name || selectedNominee.email)
    : (invitedOwner && invitedOwner.id === acct.ownerPrincipalId ? `${invitedOwner.email} (invited)` : null);

  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
        Who is accountable for this agent?
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <OwnerCard
          selected={acct.ownerMode === 'self'}
          onClick={() => update({ ownerMode: 'self', accepted: true, ownerPrincipalId: null })}
          icon={<ShieldCheck size={15} />}
          title="I'll be the accountable owner"
          desc="You accept now and the agent activates on create."
        />
        <OwnerCard
          selected={acct.ownerMode === 'nominate'}
          onClick={() => update({ ownerMode: 'nominate', accepted: false })}
          icon={<UserPlus size={15} />}
          title="Nominate someone else"
          desc="Provisioned inactive until they accept accountability."
        />
      </div>

      {acct.ownerMode === 'self' ? (
        <div className="mt-2.5">
          <div className="inline-flex items-center gap-1.5 text-[12px] text-v2-ink bg-v2-muted rounded-v2-sm px-2 py-1">
            <BadgeCheck size={13} className="text-v2-trust" /> {myName} · verified human
          </div>
          <label className="flex items-start gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!acct.accepted}
              onChange={(e) => update({ accepted: e.target.checked })}
              className="mt-0.5"
            />
            <span className="text-[12px] text-v2-ink-muted leading-snug">
              I accept accountability for this agent&apos;s actions and confirm its charter is accurate.
              Any later expansion re-hashes the charter and requires re-attestation.
            </span>
          </label>
          {!acct.accepted ? (
            <div className="text-[11.5px] text-v2-warning mt-1">
              Without acceptance the agent is created inactive (NOMINATED) — you can accept later from Outcomes &amp; Actions.
            </div>
          ) : null}
        </div>
      ) : null}

      {acct.ownerMode === 'nominate' ? (
        <div className="mt-2.5">
          {acct.ownerPrincipalId && nomineeLabel ? (
            <div className="flex items-center justify-between gap-2 mb-2 text-[12.5px]">
              <span className="text-v2-ink">Nominated: <b>{nomineeLabel}</b></span>
              <button type="button" className="text-v2-ink-muted hover:text-v2-ink text-[12px]"
                onClick={() => { update({ ownerPrincipalId: null }); setInvitedOwner(null); }}>Change</button>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-v2-ink-muted pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search people to nominate…"
                  className="v2-focus-ring w-full pl-9 pr-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[12.5px]"
                />
              </div>
              <div className="max-h-40 overflow-auto divide-y divide-v2-border border border-v2-border rounded-v2-sm">
                {filteredUsers.length === 0 ? (
                  <div className="px-3 py-2 text-[12px] text-v2-ink-muted">No people found.</div>
                ) : filteredUsers.map((u) => (
                  <button key={u.id} type="button"
                    onClick={() => update({ ownerPrincipalId: u.id })}
                    className="w-full text-left px-3 py-1.5 hover:bg-v2-muted flex items-center gap-2">
                    <span className="text-[12.5px] text-v2-ink flex-1 truncate">{u.name || u.email || u.id}</span>
                    {u.id === myId ? <span className="text-[11px] text-v2-ink-muted">you</span> : null}
                  </button>
                ))}
              </div>
              {/* Invite a NEW business user as owner (reuses platform invite + ownership email). */}
              <div className="mt-2 pt-2 border-t border-v2-border">
                {!inviteMode ? (
                  <button type="button" onClick={() => setInviteMode(true)}
                    className="text-[12px] text-v2-action hover:underline inline-flex items-center gap-1">
                    <UserPlus size={13} /> Invite a new person
                  </button>
                ) : (
                  <div className="space-y-2">
                    <input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                      placeholder="Email (required)" aria-label="Invitee email"
                      className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[12.5px]" />
                    <div className="grid grid-cols-2 gap-2">
                      <input value={invite.firstName} onChange={(e) => setInvite({ ...invite, firstName: e.target.value })}
                        placeholder="First name" aria-label="First name"
                        className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[12.5px]" />
                      <input value={invite.lastName} onChange={(e) => setInvite({ ...invite, lastName: e.target.value })}
                        placeholder="Last name" aria-label="Last name"
                        className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[12.5px]" />
                    </div>
                    {inviteError ? <div className="text-[11.5px] text-v2-danger">{inviteError}</div> : null}
                    <div className="flex items-center gap-2">
                      <Button variant="primary" size="sm" loading={inviting} iconLeft={<UserPlus size={13} />} onClick={onInvite}>
                        Invite &amp; nominate
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setInviteMode(false); setInviteError(null); }}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}

      <div className="text-[11px] text-v2-ink-muted leading-snug mt-2.5 bg-v2-muted rounded-v2-sm px-3 py-2">
        <strong className="text-v2-ink">Creation gate · deny-by-default:</strong> creator permitted · owner is a
        verified human · owner consent bound to the charter. Materiality is assessed via the platform rubric.
      </div>
    </div>
  );
}
